import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Download, 
  Mail, 
  CheckCircle, 
  ExternalLink,
  Copy,
  Ship,
  FileText,
  Monitor,
  Globe,
  Smartphone,
  ArrowLeft,
  BarChart3,
  Package,
  DollarSign,
  Users,
  TrendingUp,
  Zap,
  Settings,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";


// Plugin configurations
const PLUGIN_CONFIG = {
  operations: {
    title: "Operations Panel",
    subtitle: "For operations team - tracking, PF, transport orders",
    color: "blue",
    manifestEndpoint: "/api/outlook/download-manifest",
    addinPath: "/outlook-addin",
    features: [
      { icon: "📧", title: "Email Matching", desc: "Auto-match emails to shipments & contacts" },
      { icon: "📦", title: "Container Tracking", desc: "Real-time tracking in Outlook" },
      { icon: "👥", title: "CRM Integration", desc: "View & create contacts" },
      { icon: "✅", title: "Task Management", desc: "View pending tasks & follow-ups" },
      { icon: "📁", title: "Position Folders", desc: "Link emails to PFs" },
      { icon: "🚚", title: "Transport Orders", desc: "Track delivery status" },
      { icon: "📂", title: "Email Repository", desc: "Save important emails" },
      { icon: "💵", title: "Finance Status", desc: "Check payment status" },
      { icon: "📋", title: "Documents", desc: "Upload & manage docs" },
    ]
  },
  sales: {
    title: "Sales Panel",
    subtitle: "For sales team - quotes, offers, customer insights",
    color: "green",
    manifestEndpoint: "/api/outlook/download-manifest-sales",
    addinPath: "/outlook-addin-sales",
    features: [
      { icon: "💰", title: "Quick Quotes", desc: "Create quotes directly from emails" },
      { icon: "📊", title: "Offer Management", desc: "View & update offers" },
      { icon: "👥", title: "Customer Insights", desc: "See customer history & stats" },
      { icon: "📈", title: "Pipeline View", desc: "Track sales opportunities" },
      { icon: "📧", title: "Email Templates", desc: "Quick response templates" },
      { icon: "🎯", title: "Lead Scoring", desc: "Prioritize hot leads" },
      { icon: "💼", title: "Company Profile", desc: "Full company overview" },
      { icon: "📅", title: "Follow-ups", desc: "Schedule reminders" },
      { icon: "🔔", title: "Notifications", desc: "Quote & offer alerts" },
    ]
  },
  finance: {
    title: "Finance Panel",
    subtitle: "For finance team - invoices, payments, AP/AR",
    color: "amber",
    manifestEndpoint: "/api/outlook/download-manifest-finance",
    addinPath: "/outlook-addin-finance",
    features: [
      { icon: "🧾", title: "Invoice Matching", desc: "Match invoices to shipments" },
      { icon: "💳", title: "Payment Tracking", desc: "Track AP/AR payments" },
      { icon: "📊", title: "Financial Overview", desc: "PF-level cost summaries" },
      { icon: "📧", title: "Email to Invoice", desc: "Link emails to financial docs" },
      { icon: "✅", title: "Approval Workflows", desc: "Approve costs & invoices" },
      { icon: "📁", title: "Document Upload", desc: "Attach financial documents" },
    ]
  }
};

export default function OutlookDownloadPage() {
  const { pluginType = "operations" } = useParams();
  const [copied, setCopied] = useState(false);
  
  const config = PLUGIN_CONFIG[pluginType] || PLUGIN_CONFIG.operations;
  const otherPlugins = Object.keys(PLUGIN_CONFIG).filter(k => k !== pluginType);

  const handleDownload = () => {
    window.location.href = `${API_URL}${config.manifestEndpoint}`;
    toast.success(`Downloading ${config.title} manifest.xml...`);
  };

  const handleDownloadInstaller = () => {
    window.location.href = `${API_URL}/outlook/download-installer/${pluginType}`;
    toast.success(`Downloading ${config.title} installer...`);
  };

  const handleDownloadPowerShell = () => {
    window.location.href = `${API_URL}/outlook/download-powershell/${pluginType}`;
    toast.success(`Downloading PowerShell script...`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const colorClasses = {
    blue: {
      bg: "bg-blue-600",
      bgLight: "bg-blue-50",
      bgLighter: "bg-blue-100",
      text: "text-blue-600",
      textLight: "text-blue-700",
      border: "border-blue-200",
      hover: "hover:bg-blue-700"
    },
    green: {
      bg: "bg-green-600",
      bgLight: "bg-green-50",
      bgLighter: "bg-green-100",
      text: "text-green-600",
      textLight: "text-green-700",
      border: "border-green-200",
      hover: "hover:bg-green-700"
    }
  };
  
  const colors = colorClasses[config.color];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className={`${colors.bg} text-white py-12`}>
        <div className="max-w-4xl mx-auto px-4">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to myEuropacific
          </Link>
          <div className="flex items-center justify-center gap-3 mb-4">
            <Ship className="h-10 w-10" />
            <h1 className="text-3xl font-bold">myEuropacific</h1>
          </div>
          <h2 className="text-xl opacity-90 text-center">{config.title}</h2>
          <p className="text-center text-white/70 mt-1">{config.subtitle}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Switch Plugin Banners */}
        <div className="space-y-2 mb-6">
          {otherPlugins.map(key => {
            const otherConf = PLUGIN_CONFIG[key];
            const colorMap = { operations: "blue", sales: "green", finance: "amber" };
            const c = colorMap[key] || "slate";
            return (
              <Card key={key} className={`border-${c}-200 bg-${c}-50`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {key === 'sales' && <DollarSign className="h-5 w-5 text-green-600" />}
                      {key === 'operations' && <Package className="h-5 w-5 text-blue-600" />}
                      {key === 'finance' && <Shield className="h-5 w-5 text-amber-600" />}
                      <div>
                        <p className="font-medium text-sm">{otherConf.title}</p>
                        <p className="text-xs text-slate-500">{otherConf.subtitle}</p>
                      </div>
                    </div>
                    <Link to={`/outlook-download/${key}`}>
                      <Button variant="outline" size="sm">
                        Switch to {otherConf.title}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Download Card */}
        <Card className={`mb-8 border-2 ${colors.border} shadow-lg`}>
          <CardHeader className={`text-center ${colors.bgLight}`}>
            <CardTitle className="flex items-center justify-center gap-2 text-2xl">
              <Download className={`h-6 w-6 ${colors.text}`} />
              Download {config.title}
            </CardTitle>
            <CardDescription>
              Download and run the installer to set up the Outlook add-in
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4">
              {/* Primary: Installer Script */}
              <Button 
                size="lg" 
                onClick={handleDownloadInstaller}
                className={`px-8 py-6 text-lg gap-2 ${colors.bg} ${colors.hover}`}
                data-testid="download-installer-btn"
              >
                <Download className="h-5 w-5" />
                Download Installer (.bat)
              </Button>
              <p className="text-sm text-gray-500">
                Recommended • Windows batch script • Auto-installs the add-in
              </p>
              
              {/* Secondary options */}
              <div className="flex gap-3 pt-4 border-t w-full justify-center">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPowerShell}
                  className="gap-2"
                  data-testid="download-powershell-btn"
                >
                  <FileText className="h-4 w-4" />
                  PowerShell Script (.ps1)
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="gap-2"
                  data-testid="download-manifest-btn"
                >
                  <FileText className="h-4 w-4" />
                  Manifest Only (.xml)
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Installation Instructions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Easy Install */}
          <Card className={`border-2 ${colors.border}`}>
            <CardHeader className={colors.bgLight}>
              <CardTitle className="flex items-center gap-2">
                <Zap className={`h-5 w-5 ${colors.text}`} />
                Easy Install (Recommended)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <ol className="space-y-3 text-sm">
                <li className="flex gap-2">
                  <span className={`font-bold ${colors.text}`}>1.</span>
                  Click <strong>"Download Installer (.bat)"</strong> above
                </li>
                <li className="flex gap-2">
                  <span className={`font-bold ${colors.text}`}>2.</span>
                  Run the downloaded file (double-click)
                </li>
                <li className="flex gap-2">
                  <span className={`font-bold ${colors.text}`}>3.</span>
                  Press any key when prompted to start installation
                </li>
                <li className="flex gap-2">
                  <span className={`font-bold ${colors.text}`}>4.</span>
                  Follow the on-screen instructions
                </li>
                <li className="flex gap-2">
                  <span className={`font-bold ${colors.text}`}>5.</span>
                  Open Outlook and the add-in will appear in <strong>Get Add-ins → My Add-ins</strong>
                </li>
              </ol>
              <div className={`mt-4 p-3 ${colors.bgLighter} rounded-lg`}>
                <p className="text-xs text-slate-600">
                  <strong>Note:</strong> Windows may show a security warning. Click "More info" → "Run anyway" to proceed.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Manual Install */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-slate-600" />
                Manual Install (Advanced)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <ol className="space-y-3 text-sm">
                <li className="flex gap-2">
                  <span className="font-bold text-slate-500">1.</span>
                  Download the <strong>Manifest Only (.xml)</strong> file
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-slate-500">2.</span>
                  Open Outlook → <strong>File</strong> → <strong>Options</strong>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-slate-500">3.</span>
                  Go to <strong>Trust Center</strong> → <strong>Trust Center Settings</strong>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-slate-500">4.</span>
                  Click <strong>Trusted Add-in Catalogs</strong>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-slate-500">5.</span>
                  Add the folder where you saved the manifest
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-slate-500">6.</span>
                  Check <strong>"Show in Menu"</strong> and restart Outlook
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Features */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{config.title} Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {config.features.map((feature, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <span className="text-2xl">{feature.icon}</span>
                  <div>
                    <p className="font-medium text-sm">{feature.title}</p>
                    <p className="text-xs text-gray-500">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* PDF Guide */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Need More Help?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Button 
                variant="outline"
                onClick={() => window.open(`${API_URL}/guides/Outlook_Addin_User_Guide.pdf`, '_blank')}
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF Guide
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.open(`${API_URL}${config.addinPath}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Test Add-in in Browser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Europacific Logistika d.o.o. • support@europacific.com</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/outlook-download/operations" className={`hover:underline ${pluginType === 'operations' ? 'font-semibold text-blue-600' : ''}`}>
              Operations Panel
            </Link>
            <span>•</span>
            <Link to="/outlook-download/sales" className={`hover:underline ${pluginType === 'sales' ? 'font-semibold text-green-600' : ''}`}>
              Sales Panel
            </Link>
            <span>•</span>
            <Link to="/outlook-download/finance" className={`hover:underline ${pluginType === 'finance' ? 'font-semibold text-amber-600' : ''}`}>
              Finance Panel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
